<template>
  <component
    v-if="loadedComponent"
    :is="loadedComponent"
    :key="componentKey"
    v-bind="$attrs"
    v-on="$listeners"
  >
    <slot />
  </component>
  <div v-else-if="loading" class="themed-component-loading">
    <slot name="loading">
      <div class="animate-pulse bg-gray-200 rounded h-32"></div>
    </slot>
  </div>
  <div v-else-if="error" class="themed-component-error">
    <slot name="error" :error="error">
      <div class="text-red-500 p-4">Failed to load component: {{ componentName }}</div>
    </slot>
  </div>
</template>

<script>
/**
 * ThemedComponent - Dynamic component loader based on active theme
 *
 * This component loads the appropriate version of a component based on
 * the current theme. If the theme has an override for the component,
 * it loads the themed version; otherwise, it loads the base version.
 *
 * Usage:
 *   <ThemedComponent name="LandingPage" />
 *   <ThemedComponent name="AppHeader" @open-cart="handleOpenCart" />
 *
 * Props are passed through to the loaded component via v-bind="$attrs"
 * Events are forwarded via v-on="$listeners"
 */

import { getThemedComponent, hasThemeOverride } from './componentRegistry'

export default {
  name: 'ThemedComponent',

  inheritAttrs: false,

  props: {
    /**
     * The name of the component to load
     * Must match a key in the component registry
     */
    name: {
      type: String,
      required: true,
    },

    /**
     * Optional theme override
     * If not specified, uses window.VUE_APP_THEME
     */
    theme: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      loadedComponent: null,
      loading: true,
      error: null,
    }
  },

  computed: {
    componentName() {
      return this.name
    },

    activeTheme() {
      return this.theme || window.VUE_APP_THEME || 'westmoreland'
    },

    isOverridden() {
      return hasThemeOverride(this.name, this.activeTheme)
    },

    componentKey() {
      return `${this.name}-${this.activeTheme}`
    },
  },

  watch: {
    name: {
      immediate: true,
      handler: 'loadComponent',
    },
    theme: 'loadComponent',
  },

  methods: {
    async loadComponent() {
      this.loading = true
      this.error = null
      this.loadedComponent = null

      try {
        this.loadedComponent = await getThemedComponent(this.name, this.activeTheme)
      } catch (err) {
        console.error(`[ThemedComponent] Failed to load ${this.name}:`, err)
        this.error = err
        this.loadedComponent = null
      } finally {
        this.loading = false
      }
    },
  },

  created() {
    // Log when using a themed override (helpful for debugging)
    if (this.isOverridden) {
      console.debug(
        `[ThemedComponent] Using ${this.activeTheme} override for ${this.name}`
      )
    }
  },
}
</script>

<style scoped>
.themed-component-loading {
  min-height: 100px;
}

.themed-component-error {
  min-height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
